import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Switch } from '../components/ui/switch';
import { CreditCard, Loader2, Lock, Truck, Store, ArrowLeft, Coins, Gift, Minus, Plus } from 'lucide-react';
import { apiRequest, formatPrice } from '../lib/utils';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { PaymentBadges } from '../components/PaymentBadges';

export default function CheckoutPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { cart, fetchCart } = useCart();
    
    const [deliveryType, setDeliveryType] = useState(location.state?.deliveryType || 'pickup');
    const [paymentMethod, setPaymentMethod] = useState('sogecommerce');
    const [loading, setLoading] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [address, setAddress] = useState({
        street: '',
        city: '',
        postal_code: '',
        country: 'France'
    });
    
    // Shipping cost from cart
    const shippingCost = cart?.shipping?.price || 0;
    const totalWeight = cart?.total_weight || 0;
    
    // Loyalty points
    const [loyaltyPoints, setLoyaltyPoints] = useState(0);
    const [usePoints, setUsePoints] = useState(false);
    const [pointsToUse, setPointsToUse] = useState(0);

    useEffect(() => {
        loadLoyaltyPoints();
        loadPaymentMethods();
    }, [user]);

    const loadLoyaltyPoints = async () => {
        if (user) {
            try {
                const data = await apiRequest('/loyalty/points');
                setLoyaltyPoints(data.points || 0);
            } catch (e) {
                console.error('Error loading loyalty points:', e);
            }
        }
    };

    const loadPaymentMethods = async () => {
        try {
            const data = await apiRequest('/payment-methods');
            setPaymentMethods(data.payment_methods || []);
        } catch (e) {
            console.error('Error loading payment methods:', e);
        }
    };

    const deliveryFee = deliveryType === 'delivery' ? shippingCost : 0;
    const subtotal = cart.total + deliveryFee;
    const pointsDiscount = usePoints ? Math.min(pointsToUse, loyaltyPoints, Math.floor(subtotal)) : 0;
    const total = subtotal - pointsDiscount;

    const MIN_POINTS_TO_USE = 10; // Minimum 10 points pour utiliser

    const handlePointsToggle = (checked) => {
        setUsePoints(checked);
        if (checked && loyaltyPoints >= MIN_POINTS_TO_USE) {
            // Default to using all available points (up to the total)
            setPointsToUse(Math.min(loyaltyPoints, Math.floor(subtotal)));
        } else {
            setPointsToUse(0);
        }
    };

    const adjustPoints = (delta) => {
        const newValue = pointsToUse + delta;
        // Minimum 10 points when using, maximum = loyaltyPoints or subtotal
        if (newValue >= MIN_POINTS_TO_USE && newValue <= loyaltyPoints && newValue <= Math.floor(subtotal)) {
            setPointsToUse(newValue);
        }
    };

    // Get locale for Sogecommerce
    const getSogecommerceLocale = () => {
        const lang = i18n.language;
        if (lang.startsWith('fr')) return 'fr_FR';
        if (lang.startsWith('es')) return 'es_ES';
        return 'en_GB';
    };

    const handleCheckout = async () => {
        if (deliveryType === 'delivery') {
            if (!address.street || !address.city || !address.postal_code) {
                toast.error(t('checkout.fillDeliveryAddress'));
                return;
            }
        }

        setLoading(true);
        try {
            const originUrl = window.location.origin;
            
            // Choose endpoint based on payment method
            let endpoint = '/checkout/sogecommerce';
            
            const payload = {
                delivery_type: deliveryType,
                address: deliveryType === 'delivery' ? address : null,
                origin_url: originUrl,
                points_to_use: pointsDiscount,
                payment_method: paymentMethod
            };
            
            // Add locale for Sogecommerce
            if (paymentMethod === 'sogecommerce') {
                payload.locale = getSogecommerceLocale();
            }
            
            const data = await apiRequest(endpoint, {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            // Sogecommerce requires a POST form submission with the signed params
            if (data.form_params && data.payment_url) {
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = data.payment_url;
                form.style.display = 'none';
                Object.entries(data.form_params).forEach(([key, value]) => {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = value;
                    form.appendChild(input);
                });
                document.body.appendChild(form);
                form.submit();
                return;
            }

            // Fallback (other providers with direct URL)
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            }
        } catch (error) {
            toast.error(error.message);
            setLoading(false);
        }
    };

    if (!user) {
        navigate('/auth');
        return null;
    }

    if (cart.items.length === 0) {
        navigate('/cart');
        return null;
    }

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="checkout-page">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Button
                        variant="ghost"
                        className="rounded-full"
                        onClick={() => navigate('/cart')}
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        {t('common.backToCart')}
                    </Button>
                </div>

                <div className="flex items-center gap-3 mb-8">
                    <CreditCard className="h-8 w-8 text-primary" />
                    <h1 className="font-heading text-3xl font-bold">{t('checkout.payment')}</h1>
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                    {/* Left column - Forms */}
                    <div className="space-y-6">
                        {/* Delivery options */}
                        <Card className="glass rounded-2xl">
                            <CardHeader>
                                <CardTitle>{t('cart.deliveryMethod')}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <RadioGroup
                                    value={deliveryType}
                                    onValueChange={setDeliveryType}
                                    className="space-y-3"
                                >
                                    <div className={`flex items-center space-x-3 p-4 rounded-xl border transition-colors ${
                                        deliveryType === 'pickup' ? 'border-primary bg-primary/5' : 'border-border'
                                    }`}>
                                        <RadioGroupItem value="pickup" id="checkout-pickup" />
                                        <Label htmlFor="checkout-pickup" className="flex-1 cursor-pointer">
                                            <div className="flex items-center gap-2">
                                                <Store className="h-5 w-5" />
                                                <span className="font-medium">{t('cart.storePickup')}</span>
                                            </div>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                5 place de la République, 83780 Flayosc
                                            </p>
                                        </Label>
                                        <span className="font-semibold text-emerald-500">{t('common.free')}</span>
                                    </div>
                                    <div className={`flex items-center space-x-3 p-4 rounded-xl border transition-colors ${
                                        deliveryType === 'delivery' ? 'border-primary bg-primary/5' : 'border-border'
                                    }`}>
                                        <RadioGroupItem value="delivery" id="checkout-delivery" />
                                        <Label htmlFor="checkout-delivery" className="flex-1 cursor-pointer">
                                            <div className="flex items-center gap-2">
                                                <Truck className="h-5 w-5" />
                                                <span className="font-medium">Livraison Chronopost</span>
                                            </div>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                Livraison 24-48h • {totalWeight} kg
                                            </p>
                                        </Label>
                                        <span className="font-semibold">{formatPrice(shippingCost)}</span>
                                    </div>
                                </RadioGroup>
                            </CardContent>
                        </Card>

                        {/* Delivery address */}
                        {deliveryType === 'delivery' && (
                            <Card className="glass rounded-2xl animate-slide-up">
                                <CardHeader>
                                    <CardTitle>{t('checkout.deliveryAddress')}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div>
                                        <Label htmlFor="street">{t('checkout.address')}</Label>
                                        <Input
                                            id="street"
                                            value={address.street}
                                            onChange={(e) => setAddress(prev => ({ ...prev, street: e.target.value }))}
                                            placeholder="123 Rue de la Piscine"
                                            className="mt-1 h-12 rounded-xl"
                                            data-testid="address-street"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="city">{t('checkout.city')}</Label>
                                            <Input
                                                id="city"
                                                value={address.city}
                                                onChange={(e) => setAddress(prev => ({ ...prev, city: e.target.value }))}
                                                placeholder="Flayosc"
                                                className="mt-1 h-12 rounded-xl"
                                                data-testid="address-city"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="postal_code">{t('checkout.postalCode')}</Label>
                                            <Input
                                                id="postal_code"
                                                value={address.postal_code}
                                                onChange={(e) => setAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                                                placeholder="83780"
                                                className="mt-1 h-12 rounded-xl"
                                                data-testid="address-postal"
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Payment method */}
                        <Card className="glass rounded-2xl">
                            <CardHeader>
                                <CardTitle>{t('checkout.paymentMethod')}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <RadioGroup
                                    value={paymentMethod}
                                    onValueChange={setPaymentMethod}
                                    className="space-y-3"
                                >
                                    {/* Sogecommerce (Société Générale) */}
                                    <div
                                        className={`flex items-center space-x-3 p-4 rounded-xl border transition-colors cursor-pointer ${
                                            paymentMethod === 'sogecommerce' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                                        }`}
                                        onClick={() => setPaymentMethod('sogecommerce')}
                                        data-testid="payment-sogecommerce"
                                    >
                                        <RadioGroupItem value="sogecommerce" id="payment-sogecommerce" />
                                        <Label htmlFor="payment-sogecommerce" className="flex-1 cursor-pointer">
                                            <div className="flex items-center gap-2">
                                                <CreditCard className="h-5 w-5" />
                                                <span className="font-medium">Carte bancaire</span>
                                            </div>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                CB, Visa, Mastercard · Paiement sécurisé Société Générale
                                            </p>
                                        </Label>
                                        <div className="flex items-center gap-2">
                                            <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" className="h-5" />
                                            <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-5" />
                                            <div className="bg-red-600 text-white px-2 py-1 rounded text-xs font-bold">SG</div>
                                        </div>
                                    </div>
                                </RadioGroup>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right column - Summary */}
                    <div className="space-y-6">
                        {/* Loyalty Points - minimum 10 points required */}
                        {user && loyaltyPoints >= 10 && (
                            <Card className="glass rounded-2xl border-2 border-amber-500/30 bg-amber-500/5" data-testid="loyalty-section">
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                                        <Gift className="h-5 w-5" />
                                        Points Fidélité
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Coins className="h-5 w-5 text-amber-500" />
                                            <div>
                                                <p className="font-medium">Vous avez <span className="text-amber-500 font-bold">{loyaltyPoints} points</span></p>
                                                <p className="text-xs text-muted-foreground">= {loyaltyPoints}€ de réduction disponible (min. 10 pts)</p>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={usePoints}
                                            onCheckedChange={handlePointsToggle}
                                            data-testid="use-points-toggle"
                                        />
                                    </div>
                                    
                                    {usePoints && (
                                        <div className="flex items-center justify-between p-3 rounded-xl bg-amber-500/10 animate-slide-up">
                                            <span className="text-sm font-medium">Points à utiliser :</span>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    className="h-8 w-8 rounded-full"
                                                    onClick={() => adjustPoints(-1)}
                                                    disabled={pointsToUse <= MIN_POINTS_TO_USE}
                                                >
                                                    <Minus className="h-4 w-4" />
                                                </Button>
                                                <span className="font-bold text-lg w-12 text-center text-amber-500">{pointsToUse}</span>
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    className="h-8 w-8 rounded-full"
                                                    onClick={() => adjustPoints(1)}
                                                    disabled={pointsToUse >= loyaltyPoints || pointsToUse >= Math.floor(subtotal)}
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        <Card className="glass rounded-2xl">
                            <CardHeader>
                                <CardTitle>Récapitulatif de la commande</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Items */}
                                <div className="space-y-3">
                                    {cart.items.map(item => (
                                        <div key={item.product_id} className="flex items-center gap-3">
                                            <div className="h-12 w-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm truncate">{item.name}</p>
                                                <p className="text-xs text-muted-foreground">x{item.quantity}</p>
                                            </div>
                                            <span className="font-mono text-sm">{formatPrice(item.item_total)}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="border-t border-border pt-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Sous-total</span>
                                        <span className="font-mono">{formatPrice(cart.total)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Livraison</span>
                                        <span className={`font-mono ${deliveryFee === 0 ? 'text-emerald-500' : ''}`}>
                                            {deliveryFee === 0 ? 'Gratuit' : formatPrice(deliveryFee)}
                                        </span>
                                    </div>
                                    {pointsDiscount > 0 && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-amber-500 flex items-center gap-1">
                                                <Coins className="h-3 w-3" />
                                                Réduction fidélité ({pointsDiscount} pts)
                                            </span>
                                            <span className="font-mono text-amber-500">-{formatPrice(pointsDiscount)}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="border-t border-border pt-4 flex justify-between">
                                    <span className="font-heading font-semibold text-lg">Total</span>
                                    <span className="font-heading font-bold text-2xl text-primary" data-testid="checkout-total">
                                        {formatPrice(total)}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Pay button */}
                        <div className="space-y-4">
                            {/* Standard Card Payment via Sogecommerce */}
                            <Button
                                size="lg"
                                className="w-full rounded-full h-14 font-semibold"
                                onClick={handleCheckout}
                                disabled={loading}
                                data-testid="pay-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                        Redirection...
                                    </>
                                ) : (
                                    <>
                                        <CreditCard className="h-5 w-5 mr-2" />
                                        Payer par carte {formatPrice(total)}
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Payment Security Badges */}
                        <PaymentBadges className="mt-4" />
                    </div>
                </div>
            </div>
        </div>
    );
}
